/**
 * „Verfügbar bis" je Teamline — der Schritt vor dem Anlegen.
 *
 * 🔴 Matze, 21.09.2026: „Verfügbarkeit bis soll gesetzt werden. Muss bei der
 * Artikelanlage in Abhängigkeit der Teamline vom User angegeben werden." Eine
 * Squad-Linie läuft anders aus als eine Essential-Linie; ein Datum für den ganzen
 * Lauf wäre falsch, sobald eine Datei mehrere Linien enthält — und das ist der
 * Normalfall.
 *
 * 🔑 Gezeigt werden nur die Linien, die in DIESER Datei vorkommen, mit Artikelzahl.
 * erima führt 55 Linien; eine Liste davon wäre eine Zumutung und würde die drei, um
 * die es geht, unauffindbar machen.
 *
 * Ein leeres Feld heißt „kein Datum setzen" — nicht „Datum löschen". Der Unterschied
 * ist wichtig: `availableUntil` geht dann gar nicht mit.
 *
 * Seit 30.09.2026 geht aus dem Datum zusätzlich der Tag „Verfügbar bis Ende <Jahr>" mit
 * (Matze: Tag UND Datum). Die Spalte „Tag" zeigt, welcher — und wird rot, wenn Plenty
 * für das Jahr keinen Tag kennt; dann geht nur das Datum raus.
 *
 * 🔴 Seit 08.10.2026 wird jedes Datum SOFORT gespeichert. Vorher stand ein eingetipptes
 * Datum samt grünem Tag in der Tabelle, galt aber erst nach „Daten speichern" — 617644
 * (Equipe 29) entstand so ohne Laufzeit-Tag, obwohl die Tabelle ihn zeigte.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { CalendarClock, Check, Loader2 } from 'lucide-react';
import { api } from '@libero/core/services/api';
import LaufButton from './LaufButton';
import Info from './Info';
import { zahl } from '../lib/format';
import type { TeamlineAntwort } from '../lib/typen';

/** Eingebettet in „Für alle einstellen" (Phase 5) — kein eigener Schritt mehr. */
export default function TeamlinePanel({ entwurfId }: { entwurfId: number }) {
  const [daten, setDaten] = useState<TeamlineAntwort | null>(null);
  const [werte, setWerte] = useState<Record<string, string>>({});
  const [laedt, setLaedt] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    try {
      const antwort = await api.get<{ data: TeamlineAntwort }>(
        `/programmes/artikelimport/entwuerfe/${entwurfId}/teamlines`,
      );
      setDaten(antwort.data.data);
      setWerte(antwort.data.data.verfuegbarBis ?? {});
    } catch {
      setFehler('Die Teamlines ließen sich nicht laden.');
    } finally {
      setLaedt(false);
    }
  }, [entwurfId]);

  useEffect(() => { void laden(); }, [laden]);

  // Gespeichert wird die ganze Tabelle — nacheinander, damit eine langsame Antwort keine
  // neuere Eingabe überschreibt (die jeweils neueste Tabelle geht als letzte raus).
  const [stand, setStand] = useState<'gespeichert' | 'speichert' | 'fehler' | null>(null);
  const kette = useRef<Promise<void>>(Promise.resolve());
  // Immer der neueste Stand — zwei schnelle Änderungen an verschiedenen Linien dürfen sich
  // nicht gegenseitig überschreiben (der State in der Closure wäre veraltet).
  const aktuell = useRef<Record<string, string>>({});
  useEffect(() => { aktuell.current = werte; }, [werte]);
  function speichere(tabelle: Record<string, string>) {
    setStand('speichert');
    setFehler(null);
    setMeldung(null);
    kette.current = kette.current.then(async () => {
      try {
        const antwort = await api.patch<{ data: { verfuegbarBis: Record<string, string>; verworfen: string[] } }>(
          `/programmes/artikelimport/entwuerfe/${entwurfId}/verfuegbar-bis`,
          { verfuegbarBis: tabelle },
        );
        const { verworfen } = antwort.data.data;
        setStand('gespeichert');
        if (verworfen.length > 0) {
          setMeldung(`${zahl(verworfen.length)} Angaben wurden verworfen, weil sie kein gültiges Datum sind: ${verworfen.join(', ')}.`);
        }
      } catch {
        setStand('fehler');
        setFehler('Das Speichern ist fehlgeschlagen — das Datum gilt NICHT. Bitte erneut eingeben.');
      }
    });
  }
  function aendere(teamline: string, datum: string) {
    // Halb getippte Daten liefert das Feld als '' — gespeichert wird nur ein vollständiges
    // Datum oder bewusstes Leeren (Feld war vorher gefüllt).
    const vorher = aktuell.current[teamline] ?? '';
    const neu = { ...aktuell.current, [teamline]: datum };
    aktuell.current = neu;
    setWerte(neu);
    if (/^\d{4}-\d{2}-\d{2}$/.test(datum) || (datum === '' && vorher !== '')) speichere(neu);
  }

  const gesetzt = Object.values(werte).filter(w => String(w ?? '').trim().length > 0).length;
  const linien = daten?.teamlines ?? [];

  return (
    <div className="space-y-2">
      <h3 className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A]">
        Verfügbar bis
        <span className="font-normal text-[#6B6A66]">
          {linien.length === 0 ? '' : `${zahl(gesetzt)} von ${zahl(linien.length)} Teamlines mit Datum`}
        </span>
        <Info>
          <p>
            {daten?.quelle === 'spalte'
              ? 'Die Teamline steht in der Quelldatei (Spalte „Linie").'
              : 'Die Teamline wird am Artikelnamen erkannt — über dieselben Regeln wie die Kollektions-Kategorie.'}
          </p>
          <p>Leeres Feld = kein Datum, der Artikel läuft unbefristet.</p>
          <p>
            Aus dem Jahr des Datums folgt der Tag „Verfügbar bis Ende &lt;Jahr&gt;" — er wird zusätzlich
            gesetzt. Neue Jahre: den Tag in Plenty anlegen, das Tool findet ihn am Namen.
          </p>
        </Info>
      </h3>

      {fehler && <p className="mb-2 text-sm text-red-700">{fehler}</p>}
      {meldung && <p className="mb-2 text-sm text-green-800">{meldung}</p>}

      {laedt && <p className="text-sm text-[#1A1A1A]/60">Lädt…</p>}
      {!laedt && linien.length === 0 && <p className="text-sm text-[#6B6A66]">Keine Teamlines erkannt.</p>}

      {linien.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[#1A1A1A]/50">
              <th className="py-1 pr-3 font-medium">Teamline</th>
              <th className="py-1 pr-3 text-right font-medium">Artikel</th>
              <th className="py-1 pr-3 text-right font-medium">Varianten</th>
              <th className="py-1 pr-3 font-medium">Verfügbar bis</th>
              <th className="py-1 pr-3 font-medium">Tag</th>
              <th className="py-1 font-medium">Beispiele</th>
            </tr>
          </thead>
          <tbody>
            {linien.map(l => (
              <tr key={l.teamline} className="border-t border-[#DAD9D9]/50">
                <td className="py-1 pr-3 font-medium">{l.teamline}</td>
                <td className="py-1 pr-3 text-right tabular-nums">{zahl(l.artikel)}</td>
                <td className="py-1 pr-3 text-right tabular-nums">{zahl(l.varianten)}</td>
                <td className="py-1 pr-3">
                  <input
                    type="date"
                    value={werte[l.teamline] ?? ''}
                    onChange={e => aendere(l.teamline, e.target.value)}
                    className="rounded border border-[#DAD9D9] px-2 py-1 text-sm"
                  />
                </td>
                <td className="py-1 pr-3 text-xs">
                  <LaufzeitTagAnzeige datum={werte[l.teamline]} tags={daten?.laufzeitTags ?? null} />
                </td>
                <td className="py-1 text-xs text-[#1A1A1A]/50">
                  {l.beispiele.slice(0, 2).join(' · ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="mt-3 flex items-center gap-3">
        {stand === 'speichert' && (
          <span className="flex items-center gap-1 text-sm text-[#6B6A66]"><Loader2 className="h-4 w-4 animate-spin" />Datum wird gespeichert …</span>
        )}
        {stand === 'gespeichert' && (
          <span className="flex items-center gap-1 text-sm text-green-800"><Check className="h-4 w-4" />Gespeichert — gilt für den Lauf</span>
        )}
        <LaufButton
          laeuft={laedt}
          laufText="Teamlines werden gelesen"
          icon={<CalendarClock className="h-4 w-4" />}
          onClick={() => void laden()}
          variant="outline"
          size="sm"
        >
          Neu lesen
        </LaufButton>
      </div>
    </div>
  );
}

/** Welcher Laufzeit-Tag aus dem Datum folgt — oder dass es für das Jahr keinen gibt. */
function LaufzeitTagAnzeige({ datum, tags }: { datum?: string; tags: TeamlineAntwort['laufzeitTags'] }) {
  const jahr = /^(\d{4})-\d{2}-\d{2}$/.exec(String(datum ?? '').trim())?.[1];
  if (!jahr) return <span className="text-[#1A1A1A]/40">—</span>;
  if (tags == null) return <span className="text-[#6B6A66]">Tag-Liste nicht lesbar</span>;
  const tag = tags.find(t => t.jahr === Number(jahr));
  if (!tag) {
    return (
      <span className="text-red-700">
        Kein Tag „Verfügbar bis Ende {jahr}" in Plenty — nur das Datum wird gesetzt
      </span>
    );
  }
  return (
    <span className="inline-block rounded-full bg-[#0a9d19] px-2 py-0.5 font-medium text-white">
      {tag.name}
    </span>
  );
}
